import React from 'react';
import Dropdown, { DropdownOption } from '../../../../components/common/Dropdown';
import ErrorBanner, { ValidationError } from '../../../../components/common/ErrorBanner';
import CustomDatepicker from '../../../../components/common/DatePicker/Datepicker';

/**
 * Renders a single inline field validation error with an icon,
 * matching the pattern used in ManageTestOfChange.
 */
const ValidationErrorDisplay = React.memo<{ message: string; id: string }>(({ message, id }) => (
  <div className="error-description">
    <ul id={id} className="my-0!">
      <li>
        <img
          src="/assets/images/errorCircleSmall.svg"
          alt="Error icon"
          width="14"
          height="14"
        />
        <span className="screenreader-only">{message}</span> {message}
      </li>
    </ul>
  </div>
));
ValidationErrorDisplay.displayName = 'ValidationErrorDisplay';

export interface ManageTestDataProps {
  visible: boolean;
  validationErrors: ValidationError[];
  testDataDate?: string;
  testDataDateError?: string;
  onRangeTestDataDateChange?: (name: string, value: string) => void;
  selectedShift: DropdownOption;
  shiftOptions: DropdownOption[];
  defaultOption: DropdownOption;
  handleSelectedShiftClick: (opt: DropdownOption) => void;
  numeratorRef: React.RefObject<HTMLInputElement>;
  numeratorName: string;
  setNumeratorName: (s: string) => void;
  denominatorRef: React.RefObject<HTMLInputElement>;
  denomenatorName: string;
  setDenomenatorName: (s: string) => void;
  outSiderVarVal?: string;
  setOutSiderVarVal?: (s: string) => void;
  calculateVal: number;
  project?: any;
  notesRef: React.RefObject<HTMLTextAreaElement>;
  notes: string;
  setNotes: (s: string) => void;
  setShowAddForm: (b: boolean) => void;
  handleAddTestData: (e?: React.MouseEvent<HTMLButtonElement>, keepOpen?: boolean) => Promise<void> | void;
  sanitizeDecimalInput?: (v: string) => string;
}

const ADDITIONAL_MAX = 30;

const ManageTestData: React.FC<ManageTestDataProps> = ({
  visible,
  validationErrors,
  // formTestDate,
  // setFormTestDate,
  testDataDateError,
  testDataDate,
  onRangeTestDataDateChange,
  selectedShift,
  shiftOptions,
  defaultOption,
  handleSelectedShiftClick,
  numeratorRef,
  numeratorName,
  setNumeratorName,
  denominatorRef,
  denomenatorName,
  setDenomenatorName,
  outSiderVarVal,
  setOutSiderVarVal,
  calculateVal,
  project,
  notesRef,
  notes,
  setNotes,
  setShowAddForm,
  handleAddTestData,
  sanitizeDecimalInput,
}) => {
 
  const [numeratorFormatError, setNumeratorFormatError] = React.useState<string>('');
  const [denominatorFormatError, setDenominatorFormatError] = React.useState<string>('');

  /**
   * Derives a field→message map from the validationErrors prop so each field
   * can independently show an inline indicator, identical to ManageTestOfChange.
   */
  const fieldErrors = React.useMemo(
    () =>
      validationErrors.reduce<Record<string, string>>((acc, error) => {
        if (!acc[error.field]) acc[error.field] = error.message;
        return acc;
      }, {}),
    [validationErrors],
  );

  const combinedErrors = validationErrors;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  fiveYearsAgo.setHours(0, 0, 0, 0);

  const displayCalc = (typeof calculateVal !== 'number' || Number.isNaN(calculateVal) || calculateVal === 0)
    ? '0.00'
    : calculateVal.toFixed(4);

  const NOTES_MAX = 1000;
  const notesRemaining = Math.max(0, NOTES_MAX - (notes ? notes.length : 0));
  const additionalRemaining = Math.max(0, ADDITIONAL_MAX - ((outSiderVarVal ?? '').length));

  if (!visible) return null;

  return (
    <div className="bg-[#F2F8FB] border-solid border-2 border-border-light w-full  p-8! mt-8! mb-24!">
      {combinedErrors.length > 0 && (
        <div className="mb-4!">
          <ErrorBanner errors={combinedErrors} />
        </div>
      )}
      <div className="flex! w-1/2! gap-x-4! items-start!">
        <div
          className="ds-form w-1/2!"
          // data-ds-theme="vessel"
          // data-ds-variant="input"
        >
          <label htmlFor="date-input-testDataDate">Date of Test <span className="text-red-600!">*</span></label>
          <CustomDatepicker
            name="testDataDate"
            value={testDataDate}
            onChange={onRangeTestDataDateChange || (() => {})}
            helperText={"MM-DD-YYYY"}
            disabled={false}
            error={fieldErrors.testDate ?? testDataDateError}
          />
        </div>
        <div
          className="ds-form w-1/2!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <label htmlFor="td-shift">Shift <span className="text-red-600!">*</span></label>
          <Dropdown
            selectedValue={selectedShift}
            options={shiftOptions.length > 0 ? shiftOptions : [defaultOption]}
            onSelect={handleSelectedShiftClick}
            buttonClassName="w-full! bg-white"
            menuClassName="w-full!"
          />
          {fieldErrors.shift && (
            <ValidationErrorDisplay message={fieldErrors.shift} id="error-shift" />
          )}
        </div>
      </div>
      <div className="flex w-full! gap-x-8 mt-8! mb-8!">
        <div
          className="ds-form w-1/3!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <label htmlFor="text-numerator">{project?.numeratorName} <span className="text-red-600!">*</span></label>
          <input
            ref={numeratorRef}
            autoComplete="on"
            id="text-numerator"
            type="text"
            value={numeratorName}
            onChange={(e) => {
              const v = e.target.value;
              const sanitized = (sanitizeDecimalInput ?? ((x: string) => x))(v);
              // Show a transient alert while typing; the sanitized value is always committed to state
              const hasFormatIssue = sanitized !== v;
              setNumeratorName(sanitized);
              setNumeratorFormatError(hasFormatIssue ? 'Up to 10 digits before decimal and up to 2 after decimal.' : '');
            }}
            onBlur={() => setNumeratorFormatError('')}
            className={`mt-1! bg-white w-full! ${numeratorFormatError ? 'field-error' : ''}`}
          />
          {numeratorFormatError ? (
            <div className="error-description">
              <ul>
                <li>
                  <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                  <span className="screenreader-only">Error</span>
                  {numeratorFormatError}
                </li>
              </ul>
            </div>
          ) : null}
          {!numeratorFormatError && fieldErrors.numerator && (
            <ValidationErrorDisplay message={fieldErrors.numerator} id="error-numerator" />
          )}
        </div>
        <div
          className="ds-form w-1/3!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <label htmlFor="text-denominator">{project?.denominatorName} <span className="text-red-600!">*</span></label>
          <input
            ref={denominatorRef}
            autoComplete="on"
            id="text-denominator"
            type="text"
            value={denomenatorName}
            onChange={(e) => {
              const v = e.target.value;
              const sanitized = (sanitizeDecimalInput ?? ((x: string) => x))(v);
              const hasFormatIssue = sanitized !== v;
              setDenomenatorName(sanitized);
              setDenominatorFormatError(hasFormatIssue ? 'Up to 10 digits before decimal and up to 2 after decimal.' : '');
            }}
            onBlur={() => setDenominatorFormatError('')}
            className={`mt-1! bg-white w-full! ${denominatorFormatError ? 'field-error' : ''}`}
          />
          {denominatorFormatError ? (
            <div className="error-description">
              <ul>
                <li>
                  <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                  <span className="screenreader-only">Error</span>
                  {denominatorFormatError}
                </li>
              </ul>
            </div>
          ) : null}
          {!denominatorFormatError && fieldErrors.denominator && (
            <ValidationErrorDisplay message={fieldErrors.denominator} id="error-denominator" />
          )}
        </div>
        <div
          className="ds-form w-1/3!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <label htmlFor="text-cal">{project?.valueName}</label>
          <input
            autoComplete="on"
            id="td-calc"
            type="text"
            value={displayCalc}
            className="mt-1! bg-white w-full!"
            readOnly
          />
        </div>
         <div
          className="ds-form w-1/3!"
          data-ds-theme="vessel"
          data-ds-variant="input"
        >
          <label htmlFor="td-ousiderVar">{project?.outsideVarName != null && project.outsideVarName !== "" ? project.outsideVarName : "Other"}</label>
          <input
            autoComplete="on"
            id="td-ousiderVar"
            type="text"
            value={outSiderVarVal}
            maxLength={ADDITIONAL_MAX}
            onChange={(e) => {
              setOutSiderVarVal?.(e.target.value.slice(0, ADDITIONAL_MAX));
            }}
            className="mt-1! bg-white w-full!"
          />
          <output className="ds-form__text-input-helper mt-1 block text-sm text-gray-500" aria-live="polite">
            {`${additionalRemaining} of ${ADDITIONAL_MAX} characters left`}
          </output>
        </div>
      </div>
      <div className="form-group w-1/2 min-[992px]:col-span-2 mb-8!">
        <label htmlFor="notes">notes</label>
        <div className="textarea-wrapper w-full relative">
          <textarea
            autoComplete="on"
            id="notes"
            name="notes"
            ref={notesRef}
            value={notes}
            className="text-area_word-count text-area_global -book text-base! text-[#0D1C3D] w-full! w-full min-w-0 block bg-white  h-30!"
            style={{ width: '1700%', textTransform: 'none' }}
            rows={4}
            onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
            maxLength={NOTES_MAX}
          />
          <output className="ds-form__text-input-helper mt-1" aria-live="polite">
            {`${notesRemaining} of 1000 character left`}
          </output>
        </div>
      </div>
      <div className="flex gap-x-4 mt-4!">
        <button
          className="button py-0! -inverted"
          onClick={() => setShowAddForm(false)}
        >
          Cancel
        </button>
        <button
          className="button py-0! -inverted"
          data-analytics-type="button"
          data-analytics-click="SaveTestData"
          onClick={(e) => {
            handleAddTestData(e, false);
          }}
        >
          Save Test Data
        </button>
        <button
          className="button py-0! -inverted"
          data-analytics-type="button"
          data-analytics-click="SaveTestData"
          onClick={(e) => {
            handleAddTestData(e, true);
          }}
        >
          Save and Add Another
        </button>
      </div>
    </div>
  );
};

export default ManageTestData;
